import React, { useEffect, useRef, useState } from "react";
import { Button } from "@component/button";
import { Loader2Icon, SearchIcon, XIcon } from "lucide-react";
import { StartupCandidate } from "@type/candidate";
import { MetaPagination } from "@type/common";
import { defaultPagination } from "@constant/app";
import { EmptyState } from "@component/empty-state";
import { cn } from "@util/tailwind";
import { getStartupCandidates } from "@service/job";
import { StartupCandidateCard } from "@component/startup-candidate-card";
import { catchErrorLog } from "@util/sentry";
import LoadingBox from "@component/loading-box";
import DrawerStartupCandidateProfile from "./drawer/drawer-startup-candidate-profile";

const DEFAULT_PAGINATION = {
    page: defaultPagination.page,
    limit: defaultPagination.limit,
    total: 0,
    total_pages: 1
}

export function StartupCandidateDirectory() {
    const [candidates, setCandidates] = useState<StartupCandidate[]>([]);
    const [isLoading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [paginationMetadata, setPaginationMetadata] = useState<MetaPagination>(DEFAULT_PAGINATION)
    const [selectedCandidate, setSelectedCandidate] = useState<StartupCandidate | null>(null);
    const [showRecentSearches, setShowRecentSearches] = useState<boolean>(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const fetchCandidates = async (currentPage: number, keyword: string, isLoadMore: boolean) => {
        if (isLoadMore) {
            setIsLoadingMore(true);
        } else {
            setLoading(true);
        }

        try {
            const response = await getStartupCandidates({
                page: currentPage,
                top_k: DEFAULT_PAGINATION.limit,
                keyword: keyword,
            });

            if (response) {
                if (isLoadMore) {
                    console.log("Loading more candidates", response.data?.data);
                    setCandidates((prev: StartupCandidate[]) => {
                        return [...prev, ...response?.data?.data || []];
                    });
                } else {
                    setCandidates(response.data?.data as StartupCandidate[]);
                }
                setPaginationMetadata(response.data?.meta.pagination)
            } else {
                setCandidates([]);
                setPaginationMetadata(DEFAULT_PAGINATION)
            }
        } catch (err) {
            catchErrorLog(`Error fetching candidates, error: ${err}`);
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    };

    // Fetch candidates from API
    useEffect(() => {
        fetchCandidates(defaultPagination.page, "", false);

        setRecentSearches(() => {
            const storedSearches = localStorage.getItem("recentSearchesCandidates");
            return storedSearches ? JSON.parse(storedSearches) : [];
        });
    }, []);


    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                searchInputRef.current &&
                !searchInputRef.current.contains(event.target as Node)
            ) {
                setShowRecentSearches(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSearchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (searchQuery === "") return

        setShowRecentSearches(false);
        setIsSearching(true);

        try {
            const response = await getStartupCandidates({
                page: defaultPagination.page,
                keyword: searchQuery,
            });
            if (response) {
                setCandidates(response.data?.data);
                setHasSearched(true);
                setCurrentPage(defaultPagination.page);
            }
            setPaginationMetadata(response.data?.meta?.pagination || DEFAULT_PAGINATION);

            localStorage.setItem("recentSearchesCandidates", JSON.stringify([searchQuery, ...recentSearches]));
            setRecentSearches((prev) => {
                const updatedSearches = [searchQuery, ...prev];
                if (updatedSearches.length > 5) {
                    updatedSearches.shift(); // Remove oldest search if more than 5
                }
                return updatedSearches;
            });
        } catch (err) {
            catchErrorLog(`Error fetching candidates for recent search, error: ${err}`);
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            setIsSearching(false);
        }
    };

    const handleSaveRecentSearch = async (search: string) => {
        setSearchQuery(search);
        setShowRecentSearches(false);
        setIsSearching(true);

        try {
            const response = await getStartupCandidates({
                page: defaultPagination.page,
                keyword: search,
            });
            if (response) {
                setCandidates(response.data?.data);
                setHasSearched(true);
                setCurrentPage(defaultPagination.page);
            }

            setPaginationMetadata(response.data?.meta?.pagination || DEFAULT_PAGINATION);
        } catch (err) {
            catchErrorLog(`Error fetching candidates for recent search, error: ${err}`);
            setCandidates([]);
            setPaginationMetadata(DEFAULT_PAGINATION)
        } finally {
            setIsSearching(false);
        }
    }

    const onChangeSearch = (value: string) => {
        setSearchQuery(value)
        if (hasSearched && value === "") {
            fetchCandidates(defaultPagination.page, "", false);
            setHasSearched(false);
        }
    }

    const handleLoadMore = async () => {
        const nextPage = currentPage + 1;
        setCurrentPage((prev) => {
            return prev + 1;
        });
        await fetchCandidates(nextPage, searchQuery, true);
    };

    return (
        <>
            {/* <div className="h-full flex flex-col overflow-hidden"> */}
            <div className="flex-1 flex h-full overflow-hidden">
                <div className={cn("flex flex-col border-r border-gray-200 dark:border-gray-800 px-4 sm:px-12", {
                    "w-full": selectedCandidate === null,
                    "w-1/2": selectedCandidate !== null
                })}>
                    <div className="flex flex-col space-y-2 sm:space-y-4 mt-8 mb-6">
                        <form className="flex items-center space-x-2" onSubmit={handleSearchSubmit}>
                            <div className="relative flex-1">
                                <div className="relative flex-grow">
                                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder={"Try searching 'Python developer with 4 years experience'"}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 dark:bg-gray-800 dark:text-white"
                                        value={searchQuery}
                                        onChange={(e) => onChangeSearch(e.target.value)}
                                        onFocus={() => {
                                            if (recentSearches.length > 0) {
                                                setShowRecentSearches(true);
                                            }
                                        }}
                                    />

                                    {isSearching && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <Loader2Icon className="h-4 w-4 text-blue-600 animate-spin" />
                                        </div>
                                    )}

                                    {hasSearched && !isSearching && (
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground whitespace-nowrap">
                                                displaying {paginationMetadata.total || 0} results
                                            </span>

                                            {searchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={() => onChangeSearch("")}
                                                    className="text-muted-foreground hover:text-foreground p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                                    aria-label="Clear search"
                                                >
                                                    <XIcon className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {showRecentSearches && recentSearches.length > 0 && (
                                    <div
                                        ref={dropdownRef}
                                        className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-md shadow-lg z-10"
                                    >
                                        <div className="p-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                                            Recent Searches
                                        </div>
                                        <div>
                                            {recentSearches.map((search, index) => (
                                                <div key={index}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveRecentSearch(search)}
                                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-2"
                                                    >
                                                        <SearchIcon className="h-3 w-3 text-gray-400" />

                                                        {search}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <Button
                                type="submit"
                                variant="default"
                                disabled={isSearching || searchQuery.length === 0}
                                className="bg-blue-600 hover:bg-blue-800 h-[42px]"
                            >
                                Search
                            </Button>
                        </form>
                        {searchQuery.length > 0 && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-blue-700">
                                <div className="flex items-center">
                                    <SearchIcon className="h-3 w-3 mr-1" />

                                    <span className="font-medium">"{searchQuery}"</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 overflow-y-auto p-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                        <style>
                            {`
                                .hide-scrollbar::-webkit-scrollbar {
                                    display: none;
                                }
                            `}
                        </style>
                        <div className="hide-scrollbar"></div>
                        {isLoading ? (
                            <LoadingBox />
                        ) : (candidates || []).length > 0 ? (
                            <div className="pb-6">
                                <div className={cn("grid gap-4", {
                                    "grid-cols-1 md:grid-cols-2": selectedCandidate === null,
                                    "grid-cols-1": selectedCandidate !== null
                                })}>
                                    {candidates.map((candidate, index) => (
                                        <StartupCandidateCard
                                            key={index}
                                            isSelected={selectedCandidate?.id === candidate.id}
                                            candidate={candidate}
                                            onSelectedCandidate={() => setSelectedCandidate(candidate)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <EmptyState
                                icon={(
                                    <SearchIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
                                )}
                                title="No startup found."
                                description="We couldn't find any startup matching your search criteria."
                            />
                        )}

                        {(candidates || []).length > 0 &&
                            currentPage != paginationMetadata.total_pages && (
                                <div className="mb-6 flex justify-center">
                                    <Button
                                        variant="outline"
                                        className="text-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                        onClick={() => handleLoadMore()}
                                        disabled={isLoadingMore}
                                    >
                                        {isLoadingMore ? (
                                            <div className="flex items-center gap-2">
                                                <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
                                                Loading...
                                            </div>
                                        ) : (
                                            "Load More"
                                        )}
                                    </Button>
                                </div>
                            )}
                    </div>
                </div>
                {selectedCandidate && (
                    <div className="w-1/2">
                        <DrawerStartupCandidateProfile
                            isOpen={!!selectedCandidate}
                            candidate={selectedCandidate}
                            onClose={() => setSelectedCandidate(null)}
                        />
                    </div>
                )}
            </div>
        </>
    );
}